import React, { useEffect, useRef, useMemo } from 'react';
import { Chessground as NativeChessground } from 'chessground';
import type { Api } from 'chessground/api';
import type { Config } from 'chessground/config';
import type { Key, Piece } from 'chessground/types';

export interface ChessgroundProps extends Partial<Config> {
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
  onChange?: () => void;
  onMove?: (orig: Key, dest: Key) => void;
  onDropNewPiece?: (piece: Piece, key: Key) => void;
  onSelect?: (key: Key) => void;
}

const Chessground: React.FC<ChessgroundProps> = ({
  width,
  height,
  style,
  onChange,
  onMove,
  onDropNewPiece,
  onSelect,
  ...config
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);

  const events = useMemo(() => {
    const eventHandlers: Config['events'] = {};
    
    if (onChange) eventHandlers.change = onChange;
    if (onMove) eventHandlers.move = onMove;
    if (onDropNewPiece) eventHandlers.dropNewPiece = onDropNewPiece;
    if (onSelect) eventHandlers.select = onSelect;
    
    return eventHandlers;
  }, [onChange, onMove, onDropNewPiece, onSelect]);

  const fullConfig = useMemo(() => ({
    ...config,
    events,
  }), [config, events]);

  useEffect(() => {
    if (elementRef.current && !apiRef.current) {
      apiRef.current = NativeChessground(elementRef.current, fullConfig);
    }
    
    return () => {
      if (apiRef.current) {
        apiRef.current.destroy();
        apiRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.set(fullConfig);
    }
  }, [fullConfig]);

  const containerStyle = useMemo(() => ({
    ...style,
    ...(width && { width }),
    ...(height && { height }),
  }), [style, width, height]);

  return <div ref={elementRef} style={containerStyle} />;
};

export default Chessground;